/**
 * PROJ-051 Phase 3A: Keyboard Shortcuts Hook
 * 
 * Provides keyboard shortcuts for Network Diagnostics module
 * and global navigation shortcuts.
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
}

export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[] = []) {
  // Wave 21: Ensure shortcuts is always an array to prevent "not iterable" errors
  const safeShortcuts = Array.isArray(shortcuts) ? shortcuts : [];

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Guard against undefined event.key (can happen with some special keys)
      if (!event.key || safeShortcuts.length === 0) return;

      for (const shortcut of safeShortcuts) {
        const ctrlMatch = shortcut.ctrl === undefined || shortcut.ctrl === event.ctrlKey;
        const shiftMatch = shortcut.shift === undefined || shortcut.shift === event.shiftKey;
        const altMatch = shortcut.alt === undefined || shortcut.alt === event.altKey;
        const metaMatch = shortcut.meta === undefined || shortcut.meta === event.metaKey;
        const keyMatch = shortcut.key.toLowerCase() === event.key.toLowerCase();

        if (ctrlMatch && shiftMatch && altMatch && metaMatch && keyMatch) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [safeShortcuts]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Global keyboard shortcuts for application navigation
 */
export function useGlobalKeyboardShortcuts() {
  const navigate = useNavigate();

  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'n',
      ctrl: true,
      shift: true,
      description: 'Open Network Diagnostics',
      action: () => navigate('/network-diagnostics'),
    },
  ];

  useKeyboardShortcuts(shortcuts);
}

